import {
  RefreshCcw,
  Banknote,
  Wallet,
  IndianRupee,
  BadgePercent,
  Clock3,
  ShieldCheck,
  CheckCircle2,
  HandCoins,
  Briefcase,
  Landmark,
  ArrowRightLeft,
  Users,
  FileText,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Loan Takeover & Top-Up",
      path: "/takeover-top-up",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Loan Takeover & Top-Up Loan | Balance Transfer | Money Mart",

  description:
    "Transfer your existing loan to leading Banks & NBFCs for better interest rates and get additional Top-Up funding. Compare Loan Takeover & Top-Up offers with expert assistance from Money Mart.",

  keywords:
    "Loan Takeover, Loan Balance Transfer, Top Up Loan, Home Loan Balance Transfer, Mortgage Loan Takeover, Business Loan Takeover, Personal Loan Balance Transfer, Money Mart",

  canonical: "/takeover-top-up",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Loan Balance Transfer",

  title: "Reduce Your EMI & Get Additional Funds with Loan Takeover + Top-Up",

  description:
    "Already servicing a loan? Transfer it to a leading Bank or NBFC for better interest rates, improved repayment terms, and the opportunity to avail additional Top-Up funding—all with expert guidance from Money Mart.",

  highlights: [
    "Lower Interest Rates",
    "Additional Top-Up Loan",
    "Reduced EMI Options",
    "Quick Processing",
  ],

  image: "/images/loans/takeover_topup.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Loan Takeover + Top-Up",

  title: "Improve Your Existing Loan with Better Terms",

  description:
    "A Loan Takeover (Balance Transfer) allows you to shift your existing loan from one lender to another offering more competitive interest rates or repayment terms. Eligible borrowers may also receive additional Top-Up funding over the outstanding loan amount without applying for an entirely new loan.",

  items: [
    {
      icon: RefreshCcw,
      title: "Transfer Existing Loan",
      description:
        "Move your existing loan to another Bank or NBFC offering better financial terms.",
    },
    {
      icon: BadgePercent,
      title: "Lower Interest Rates",
      description:
        "Potentially reduce your borrowing cost by switching to a lender offering competitive interest rates.",
    },
    {
      icon: Banknote,
      title: "Unlimited Top-Up Funding",
      description:
        "Eligible borrowers may obtain extra funds along with the loan takeover with up to 75% of property market value, subject to lender policies.",
    },
    {
      icon: Wallet,
      title: "Improve Cash Flow",
      description:
        "Reduce your EMI burden or unlock additional liquidity for personal or business needs.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose Loan Takeover + Top-Up?",

  description:
    "Loan Takeover with Top-Up helps borrowers optimize existing loans by reducing borrowing costs while providing access to additional funding when required.",

  items: [
    {
      icon: BadgePercent,
      title: "Lower Interest Rates",
      description:
        "Switch to lenders offering competitive interest rates and potentially reduce your overall borrowing cost.",
    },
    {
      icon: IndianRupee,
      title: "Lower Monthly EMI",
      description:
        "Reduce your monthly repayment burden through improved loan terms, subject to lender approval.",
    },
    {
      icon: Banknote,
      title: "Additional Loan Amount",
      description:
        "Receive extra funding over your outstanding loan without applying for a completely new loan.",
    },
    {
      icon: Clock3,
      title: "Quick Processing",
      description:
        "Our experts help simplify documentation and coordinate with lenders for faster processing.",
    },
    {
      icon: ShieldCheck,
      title: "Flexible Repayment Options",
      description:
        "Choose repayment tenures that better align with your financial goals and cash flow.",
    },
    {
      icon: CheckCircle2,
      title: "Expert Assistance",
      description:
        "Compare multiple Banks & NBFCs to identify the most suitable Loan Takeover and Top-Up solution.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Solutions",

  title: "Loan Takeover & Top-Up Solutions We Help Arrange",

  description:
    "Money Mart helps eligible borrowers compare Loan Takeover and Top-Up solutions across multiple loan categories from leading Banks & NBFCs.",

  items: [
    {
      icon: Landmark,
      title: "Home Loan Takeover",
      description:
        "Transfer your existing Home Loan to another lender for better interest rates, lower EMIs and eligible Top-Up funding.",
    },
    {
      icon: HandCoins,
      title: "Loan Against Property Takeover",
      description:
        "Transfer your existing Loan Against Property or Mortgage Loan while exploring additional funding options.",
    },
    {
      icon: Briefcase,
      title: "Business Loan Takeover",
      description:
        "Move your existing Business Loan to another lender offering improved repayment terms and Top-Up eligibility.",
    },
    {
      icon: ArrowRightLeft,
      title: "Other Eligible Loan Transfers",
      description:
        "Depending on lender policies, takeover and Top-Up facilities may also be available for other eligible secured and unsecured loans.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Loan Takeover & Top-Up Eligibility",

  description:
    "Eligibility depends on your existing loan repayment history, outstanding loan amount, income profile, credit score, property or business profile (where applicable), and the lender's internal credit policy.",

  requirements: [
    {
      icon: RefreshCcw,
      title: "Existing Loan",
      value:
        "An active loan with satisfactory repayment history and eligible for takeover",
    },
    {
      icon: IndianRupee,
      title: "Income",
      value: "Stable and Verifiable Source of Income",
    },
    {
      icon: Wallet,
      title: "Repayment Capacity",
      value: "Adequate repayment capacity as per lender eligibility criteria",
    },
    {
      icon: ShieldCheck,
      title: "Credit Profile",
      value: "Good Credit Score and Consistent Repayment Record",
    },
  ],

  approvalFactors: [
    "Outstanding loan amount",
    "Existing lender repayment history",
    "Credit score and credit profile",
    "Monthly income and repayment capacity",
    "Current property or business valuation (where applicable)",
    "Eligibility for Top-Up funding as per lender policy",
  ],

  cta: {
    title: "Want to know your eligibility?",

    description:
      "Our experts will evaluate your existing loan and help you compare Takeover & Top-Up offers from leading Banks & NBFCs.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for Loan Takeover & Top-Up",

  description:
    "Documentation requirements vary depending on the loan being transferred and the lender. The following documents are commonly required.",

  groups: [
    {
      icon: Users,
      title: "Applicant Documents",

      documents: [
        "Passport Size Photograph",
        "PAN Card",
        "Aadhaar Card",
        "Address Proof",
      ],
    },
    {
      icon: Briefcase,
      title: "Income Documents",

      documents: [
        "Latest Salary Slips (Salaried Applicants)",
        "Income Tax Returns (Self-Employed Applicants)",
        "Business Financial Statements (if applicable)",
        "Bank Statements",
      ],
    },
    {
      icon: RefreshCcw,
      title: "Existing Loan Documents",

      documents: [
        "Loan Sanction Letter",
        "Latest Loan Statement",
        "Outstanding Balance Certificate",
        "Repayment Track Record",
      ],
    },
    {
      icon: Landmark,
      title: "Property / Security Documents",

      documents: [
        "Property Documents (for secured loans)",
        "Title Documents",
        "Existing Mortgage Documents (if applicable)",
      ],
    },
    {
      icon: FileText,
      title: "Additional Documents",

      documents: [
        "NOC or Foreclosure Letter (if required)",
        "Any Additional Documents Requested by the Lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Complete Your Loan Takeover & Top-Up in 6 Simple Steps",

  description:
    "From evaluating your existing loan and comparing lender offers to completing the takeover process and disbursing additional Top-Up funds, Money Mart assists you throughout the journey.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Loan Takeover & Top-Up?",

  description:
    "We help you compare Takeover and Top-Up offers from leading Banks & NBFCs, enabling you to reduce borrowing costs, access additional funding, and enjoy a smooth transition to your new lender.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Loan Takeover & Top-Up FAQs",

  description:
    "Find answers to the most commonly asked questions about Loan Takeover (Balance Transfer) and Top-Up Loans.",

  items: [
    {
      question: "What is a Loan Takeover or Balance Transfer?",

      answer:
        "A Loan Takeover (also called a Balance Transfer) allows you to transfer your existing loan from one lender to another offering better interest rates, improved repayment terms, or additional benefits.",
    },
    {
      question: "What is a Top-Up Loan?",

      answer:
        "A Top-Up Loan is an additional loan amount provided over your existing outstanding loan, subject to your eligibility, repayment history, and the lender's internal policies.",
    },
    {
      question: "Can I get both a Loan Takeover and a Top-Up together?",

      answer:
        "Yes. Many Banks and NBFCs offer eligible borrowers the option to transfer their existing loan while also availing additional Top-Up funding, subject to lender approval.",
    },
    {
      question: "Which types of loans can be transferred?",

      answer:
        "Depending on lender policies, takeover facilities are commonly available for Home Loans, Loan Against Property, Mortgage Loans, Business Loans, and certain other eligible loan products.",
    },
    {
      question: "What factors affect approval?",

      answer:
        "Approval depends on your repayment history, credit score, outstanding loan amount, income, repayment capacity, property or business profile (where applicable), and the lender's credit assessment.",
    },
    {
      question: "How long does the takeover process take?",

      answer:
        "The timeline depends on document verification, foreclosure formalities with the existing lender, and the new lender's processing time. Complete documentation generally results in faster approvals.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Lower Your Borrowing Cost",

  title: "Compare Loan Takeover & Top-Up Offers from Leading Banks & NBFCs",

  description:
    "Let Money Mart help you transfer your existing loan to a better lender while exploring additional Top-Up funding with competitive interest rates and expert assistance.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Loan Takeover",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Consultation",

  title: "Speak to a Loan Takeover Expert",

  description:
    "Our experts will evaluate your existing loan, compare offers from multiple Banks & NBFCs, and help you secure the most suitable Takeover and Top-Up solution.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our loan specialist will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Topup Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-1L",
        label: "₹50,000 – ₹1,00,000",
      },
      {
        value: "1L-2L",
        label: "₹1,00,000 – ₹2,00,000",
      },
      {
        value: "2L-plus",
        label: "Above ₹2,00,000",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your Loan Takeover & Top-Up enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const takeoverTopUp = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default takeoverTopUp;
